import React, { useEffect, useMemo, useState } from "react";
import api, { errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Eye, EyeOff, Copy, Pencil, Trash2, Plus, Search, Globe, Download, Lock, Vault, Shield } from "lucide-react";
import StatusBadge from "@/components/StatusBadge";
import CredentialFormDialog from "@/components/CredentialFormDialog";
import ConfirmPasswordDialog from "@/components/ConfirmPasswordDialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

function formatDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("pt-BR"); } catch { return "—"; }
}

function copy(text, label = "Conteúdo") {
  if (!text) { toast.error("Vazio"); return; }
  navigator.clipboard.writeText(text).then(
    () => toast.success(`${label} copiado`),
    () => toast.error("Falha ao copiar")
  );
}

export default function CredentialsPage() {
  const { user: me } = useAuth();
  const isAdmin = me?.role === "admin";
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const [groups, setGroups] = useState([]);
  const [assetTypes, setAssetTypes] = useState([]);
  const [revealed, setRevealed] = useState({});
  const [search, setSearch] = useState("");
  const [fCat, setFCat] = useState("all");
  const [fGroup, setFGroup] = useState("all");
  const [fType, setFType] = useState("all");
  const [fStatus, setFStatus] = useState("all");
  const [fProvider, setFProvider] = useState("");

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [toDelete, setToDelete] = useState(null);

  // Reveal confirm modal
  const [revealTarget, setRevealTarget] = useState(null);
  const [revealMode, setRevealMode] = useState("view"); // 'view' | 'copy'
  const [revealLoading, setRevealLoading] = useState(false);
  const [revealError, setRevealError] = useState("");

  // Export modal
  const [exportOpen, setExportOpen] = useState(false);
  const [exportFormat, setExportFormat] = useState("csv");
  const [exportSecrets, setExportSecrets] = useState(false);
  const [exportPassword, setExportPassword] = useState("");
  const [exporting, setExporting] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = {};
      if (search.trim()) params.search = search.trim();
      if (fCat !== "all") params.category_id = fCat;
      if (fGroup !== "all") params.group_id = fGroup;
      if (fType !== "all") params.asset_type = fType;
      if (fProvider.trim()) params.provider = fProvider.trim();
      if (fStatus !== "all") params.status = fStatus;
      const { data } = await api.get("/credentials", { params });
      setItems(data);
    } catch (e) {
      toast.error(errMsg(e, "Erro ao carregar"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    api.get("/categories").then(({ data }) => setCategories(data));
    api.get("/groups").then(({ data }) => setGroups(data));
    api.get("/meta/asset-types").then(({ data }) => setAssetTypes(data));
  }, []);

  useEffect(() => { fetchData(); /* eslint-disable-next-line */ }, [search, fCat, fGroup, fType, fStatus, fProvider]);

  const openReveal = (cred, mode = "view") => {
    if (revealed[cred.id] && mode === "view") {
      setRevealed((r) => { const c = { ...r }; delete c[cred.id]; return c; });
      return;
    }
    setRevealError("");
    setRevealTarget(cred);
    setRevealMode(mode);
  };

  const confirmReveal = async (password) => {
    if (!revealTarget) return;
    setRevealLoading(true);
    setRevealError("");
    try {
      const { data } = await api.post(`/credentials/${revealTarget.id}/reveal`, { password });
      if (revealMode === "view") {
        setRevealed((r) => ({ ...r, [revealTarget.id]: data.secret }));
        toast.success("Senha revelada (registrado na auditoria)");
      } else {
        await navigator.clipboard.writeText(data.secret);
        toast.success("Senha copiada para a área de transferência");
      }
      setRevealTarget(null);
    } catch (e) {
      setRevealError(errMsg(e, "Erro ao confirmar"));
    } finally {
      setRevealLoading(false);
    }
  };

  const onDelete = async () => {
    if (!toDelete) return;
    try {
      await api.delete(`/credentials/${toDelete.id}`);
      toast.success("Credencial removida");
      setToDelete(null);
      fetchData();
    } catch (e) {
      toast.error(errMsg(e, "Erro ao excluir"));
    }
  };

  const startExport = () => {
    setExportFormat("csv");
    setExportSecrets(false);
    setExportPassword("");
    setExportOpen(true);
  };

  const submitExport = async (e) => {
    e.preventDefault();
    if (exportSecrets && !exportPassword) {
      toast.error("Confirme sua senha de admin para exportar com senhas em texto");
      return;
    }
    setExporting(true);
    try {
      const res = await api.post(
        "/credentials/export",
        { format: exportFormat, include_secrets: exportSecrets, password: exportSecrets ? exportPassword : null },
        { responseType: "blob" }
      );
      const blob = new Blob([res.data], {
        type: exportFormat === "csv"
          ? "text/csv; charset=utf-8"
          : "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      const stamp = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
      a.href = url;
      a.download = `credenciais_via_permuta_${stamp}.${exportFormat}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Exportação concluída");
      setExportOpen(false);
    } catch (err) {
      // For blob requests, error response is also blob - need to parse
      let msg = "Erro ao exportar";
      if (err?.response?.data instanceof Blob) {
        try {
          const text = await err.response.data.text();
          const j = JSON.parse(text);
          msg = j.detail || msg;
        } catch {}
      } else {
        msg = errMsg(err, msg);
      }
      toast.error(msg);
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="space-y-6 w-full min-w-0 max-w-full" data-testid="credentials-page">
      <div className="flex items-start justify-between gap-4 flex-wrap min-w-0">
        <div className="min-w-0">
          <div className="text-[10px] tracking-[0.2em] uppercase font-semibold" style={{ color: "#FF8A00" }}>Repositório</div>
          <h2 className="font-display font-bold text-3xl mt-1 text-[#1F1F24]">Credenciais</h2>
          <p className="text-gray-600 text-sm mt-1">{items.length} {items.length === 1 ? "credencial" : "credenciais"} visível{items.length === 1 ? "" : "is"} para você.</p>
        </div>
        <div className="flex gap-2 flex-wrap shrink-0">
          {isAdmin && (
            <Button variant="outline" className="h-11 rounded-lg" onClick={startExport} data-testid="btn-export">
              <Download className="w-4 h-4 mr-2" /> Exportar
            </Button>
          )}
          <Button className="vp-btn-primary text-white h-11 px-5 rounded-lg" onClick={() => { setEditing(null); setDialogOpen(true); }} data-testid="btn-new-credential">
            <Plus className="w-4 h-4 mr-2" /> Nova credencial
          </Button>
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-xl p-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-3 min-w-0 max-w-full">
        <div className="relative lg:col-span-2 min-w-0">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Buscar..." className="pl-9" data-testid="cred-search-input" />
        </div>
        <Select value={fGroup} onValueChange={setFGroup}>
          <SelectTrigger data-testid="cred-filter-group"><SelectValue placeholder="Cofre" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos cofres</SelectItem>
            {groups.map((g) => <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={fCat} onValueChange={setFCat}>
          <SelectTrigger data-testid="cred-filter-category"><SelectValue placeholder="Categoria" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todas categorias</SelectItem>
            {categories.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={fType} onValueChange={setFType}>
          <SelectTrigger data-testid="cred-filter-type"><SelectValue placeholder="Tipo" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos tipos</SelectItem>
            {assetTypes.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={fStatus} onValueChange={setFStatus}>
          <SelectTrigger data-testid="cred-filter-status"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos status</SelectItem>
            <SelectItem value="active">Ativo</SelectItem>
            <SelectItem value="expiring">Expira em breve</SelectItem>
            <SelectItem value="expired">Expirado</SelectItem>
            <SelectItem value="no_date">Sem data</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden w-full min-w-0 max-w-full">
        <div className="w-full max-w-full overflow-x-auto overscroll-x-contain">
          <table className="w-full min-w-[960px] table-fixed">
            <colgroup>
              <col className="w-[23%]" />
              <col className="w-[12%]" />
              <col className="w-[11%]" />
              <col className="w-[16%]" />
              <col className="w-[14%]" />
              <col className="w-[10%]" />
              <col className="w-[9%]" />
              <col className="w-[92px]" />
            </colgroup>
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <Th>Nome</Th>
                <Th>Cofre</Th>
                <Th>Tipo</Th>
                <Th>Usuário</Th>
                <Th>Senha/Token</Th>
                <Th>Expira em</Th>
                <Th>Status</Th>
                <Th className="text-right pr-5 sticky right-0 z-[1] bg-gray-50 shadow-[-8px_0_12px_-12px_rgba(15,23,42,0.45)]">Ações</Th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={8} className="text-center text-gray-500 py-10" data-testid="cred-loading">Carregando...</td></tr>
              ) : items.length === 0 ? (
                <tr><td colSpan={8} className="text-center text-gray-500 py-10" data-testid="cred-empty">Nenhuma credencial visível para você</td></tr>
              ) : items.map((c) => (
                <tr key={c.id} className="border-b border-gray-100 hover:bg-gray-50" data-testid={`cred-row-${c.id}`}>
                  <Td>
                    <div className="font-semibold text-[#1F1F24] truncate" title={c.name}>{c.name}</div>
                    <div className="text-xs text-gray-500 flex items-center gap-1.5 min-w-0">
                      {c.url ? (
                        <a href={c.url} target="_blank" rel="noreferrer" className="hover:text-[#FF8A00] inline-flex items-center gap-1 min-w-0 max-w-full">
                          <Globe className="w-3 h-3 shrink-0" /> <span className="truncate">{c.company || c.provider || c.url.replace(/^https?:\/\//, "").split("/")[0]}</span>
                        </a>
                      ) : (c.company || c.provider || "—")}
                    </div>
                  </Td>
                  <Td>
                    {c.group_name ? (
                      <span className="vp-pill max-w-full" style={{ background: "#FFE4CC", color: "#9A4700" }}>
                        <Vault className="w-3 h-3 shrink-0" /> <span className="truncate">{c.group_name}</span>
                      </span>
                    ) : <span className="text-gray-400 text-xs">—</span>}
                  </Td>
                  <Td>{c.asset_type}</Td>
                  <Td>
                    <div className="flex items-center gap-1.5">
                      <span className="text-sm">{c.username || c.email || <span className="text-gray-400">—</span>}</span>
                      {(c.username || c.email) && (
                        <button onClick={() => copy(c.username || c.email, c.username ? "Usuário" : "E-mail")}
                          className="text-gray-400 hover:text-[#FF8A00] shrink-0" data-testid={`cred-copy-user-${c.id}`} title="Copiar">
                          <Copy className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  </Td>
                  <Td>
                    <div className="flex items-center gap-2 font-mono text-sm min-w-0">
                      <span className={`${revealed[c.id] ? "truncate" : "tracking-widest"} min-w-0`} data-testid={`cred-secret-${c.id}`}>
                        {revealed[c.id] ? revealed[c.id] : "********"}
                      </span>
                      {c.can_view_secret ? (
                        <>
                          <button onClick={() => openReveal(c, "view")} className="text-gray-400 hover:text-[#FF8A00] shrink-0"
                            data-testid={`cred-reveal-${c.id}`} title={revealed[c.id] ? "Ocultar" : "Visualizar"}>
                            {revealed[c.id] ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                          </button>
                          <button onClick={() => openReveal(c, "copy")} className="text-gray-400 hover:text-[#FF8A00] shrink-0"
                            data-testid={`cred-copy-secret-${c.id}`} title="Copiar senha (requer confirmação)">
                            <Copy className="w-3.5 h-3.5" />
                          </button>
                        </>
                      ) : (
                        <span className="text-gray-400" title="Você não tem permissão para visualizar a senha">
                          <Lock className="w-3.5 h-3.5" />
                        </span>
                      )}
                    </div>
                  </Td>
                  <Td>{formatDate(c.expiration_date)}</Td>
                  <Td><StatusBadge status={c.status} testId={`cred-status-${c.id}`} /></Td>
                  <Td className="text-right pr-5 sticky right-0 bg-white shadow-[-8px_0_12px_-12px_rgba(15,23,42,0.45)]">
                    <div className="inline-flex gap-1 justify-end">
                      {c.can_edit && (
                        <button onClick={() => { setEditing(c); setDialogOpen(true); }} className="p-1.5 text-gray-500 hover:text-[#FF8A00] hover:bg-orange-50 rounded-md"
                          data-testid={`cred-edit-${c.id}`} title="Editar">
                          <Pencil className="w-4 h-4" />
                        </button>
                      )}
                      {isAdmin && (
                        <button onClick={() => setToDelete(c)} className="p-1.5 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-md"
                          data-testid={`cred-delete-${c.id}`} title="Excluir">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <CredentialFormDialog
        open={dialogOpen}
        onOpenChange={(o) => { setDialogOpen(o); if (!o) setEditing(null); }}
        editing={editing}
        categories={categories}
        groups={groups}
        assetTypes={assetTypes}
        onSaved={fetchData}
      />

      <ConfirmPasswordDialog
        open={!!revealTarget}
        onOpenChange={(o) => { if (!o) { setRevealTarget(null); setRevealError(""); } }}
        title={revealMode === "copy" ? "Confirme para copiar a senha" : "Confirme para visualizar a senha"}
        description="Por segurança, digite novamente sua senha de login. Esta ação será registrada na auditoria."
        confirmLabel={revealMode === "copy" ? "Copiar senha" : "Visualizar"}
        loading={revealLoading}
        errorMessage={revealError}
        onConfirm={confirmReveal}
      />

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir credencial</AlertDialogTitle>
            <AlertDialogDescription>
              Esta ação não pode ser desfeita. A credencial <strong>{toDelete?.name}</strong> será removida permanentemente.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cred-delete-cancel">Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={onDelete} data-testid="cred-delete-confirm" className="bg-red-600 hover:bg-red-700">Excluir</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Export Dialog */}
      <Dialog open={exportOpen} onOpenChange={setExportOpen}>
        <DialogContent className="max-w-md" data-testid="export-dialog">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">
              <Download className="w-5 h-5" /> Exportar credenciais
            </DialogTitle>
            <DialogDescription className="text-sm text-gray-500">
              Apenas administradores podem exportar. Senhas em texto exigem confirmação adicional.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submitExport} className="space-y-4">
            <div>
              <Label>Formato</Label>
              <Select value={exportFormat} onValueChange={setExportFormat}>
                <SelectTrigger data-testid="export-format-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="csv">CSV (compatível Excel)</SelectItem>
                  <SelectItem value="xlsx">Excel (.xlsx)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-start gap-3 p-3 rounded-lg border" style={{ borderColor: exportSecrets ? "#FCA5A5" : "#E5E7EB", background: exportSecrets ? "#FEF2F2" : "transparent" }}>
              <Checkbox
                checked={exportSecrets}
                onCheckedChange={(v) => { setExportSecrets(!!v); if (!v) setExportPassword(""); }}
                data-testid="export-include-secrets"
                id="exp-secrets"
              />
              <div>
                <Label htmlFor="exp-secrets" className="cursor-pointer flex items-center gap-1.5 m-0">
                  <Shield className="w-3.5 h-3.5" />
                  Incluir senhas/tokens em texto aberto
                </Label>
                <p className="text-xs text-gray-500 mt-0.5">
                  Não recomendado. Por padrão senhas NÃO são exportadas.
                </p>
              </div>
            </div>
            {exportSecrets && (
              <div>
                <Label htmlFor="exp-pwd">Confirme sua senha de administrador</Label>
                <Input
                  id="exp-pwd"
                  type="password"
                  value={exportPassword}
                  onChange={(e) => setExportPassword(e.target.value)}
                  placeholder="Senha do admin logado"
                  data-testid="export-password-input"
                  required={exportSecrets}
                  autoFocus
                />
              </div>
            )}
            <DialogFooter className="gap-2">
              <Button type="button" variant="outline" onClick={() => setExportOpen(false)} data-testid="export-cancel">Cancelar</Button>
              <Button type="submit" disabled={exporting} className="vp-btn-primary text-white" data-testid="export-submit">
                {exporting ? "Exportando..." : "Exportar"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const Th = ({ children, className = "" }) => (
  <th className={`text-left text-xs uppercase tracking-wider text-gray-500 font-semibold py-3 px-3 xl:px-4 ${className}`}>{children}</th>
);
const Td = ({ children, className = "" }) => (
  <td className={`py-3 px-3 xl:px-4 text-sm text-[#1F1F24] align-middle overflow-hidden ${className}`}>{children}</td>
);
